import React, { useEffect, useState } from "react";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "../components/ui/table";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { useToast } from "../hooks/use-toast";
import { Truck, RefreshCw } from "lucide-react";
import api from "../lib/api";
import { Button } from "../components/ui/button";

interface LogisticsPartner {
	id: number;
	name: string;
	active: boolean;
	sp_id: number;
	created_at: string;
	updated_at: string;
	updated_by: number | null;
}

const LogisticsPartners: React.FC = () => {
	const [partners, setPartners] = useState<LogisticsPartner[]>([]);
	const [loading, setLoading] = useState(true);
	const [togglingId, setTogglingId] = useState<number | null>(null);
	const { toast } = useToast();

	useEffect(() => {
		fetchPartners();
	}, []);

	const fetchPartners = async () => {
		setLoading(true);
		try {
			const response = await api.getLogisticsPartners();
			if (response?.success) {
				setPartners(response.data || []);
			} else {
				toast({
					variant: "destructive",
					title: "Error",
					description: response?.error || "Failed to fetch logistics partners",
				});
			}
		} catch (error) {
			toast({
				variant: "destructive",
				title: "Error",
				description: "Failed to fetch logistics partners",
			});
		} finally {
			setLoading(false);
		}
	};

	const handleToggle = async (partner: LogisticsPartner, newActive: boolean) => {
		setTogglingId(partner.id);
		try {
			const response = await api.toggleLogisticsPartner(partner.id, newActive);
			if (response?.success) {
				setPartners((prev) =>
					prev.map((p) =>
						p.id === partner.id ? { ...p, active: newActive } : p,
					),
				);
				toast({
					title: "Success",
					description: `${partner.name} has been ${newActive ? "activated" : "deactivated"}.`,
				});
			} else {
				toast({
					variant: "destructive",
					title: "Error",
					description: response?.error || "Failed to update partner status",
				});
			}
		} catch (error) {
			toast({
				variant: "destructive",
				title: "Error",
				description: "Failed to update partner status",
			});
		} finally {
			setTogglingId(null);
		}
	};

	const formatDate = (dateString: string) => {
		if (!dateString) return "—";
		return new Date(dateString).toLocaleString("en-IN", {
			day: "2-digit",
			month: "short",
			year: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	const activeCount = partners.filter((p) => p.active).length;
	const inactiveCount = partners.length - activeCount;

	return (
		<div className="p-6 min-h-screen bg-white dark:bg-gray-900">
			{/* Header */}
			<div className="flex justify-between items-start mb-8">
				<div>
					<h1 className="text-2xl font-bold dark:text-white flex items-center gap-2">
						<Truck className="w-6 h-6 text-blue-600 dark:text-blue-400" />
						Logistics Partners
					</h1>
					<p className="text-gray-500 dark:text-gray-400 mt-1 text-sm">
						Enable or disable logistics partners to control which ones are used for
						live inventory and SLA calculations.
					</p>
				</div>
				<Button
					variant="outline"
					size="sm"
					onClick={fetchPartners}
					disabled={loading}
					className="flex items-center gap-2 dark:border-gray-600 dark:text-gray-300"
				>
					<RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
					Refresh
				</Button>
			</div>

			{/* Stats summary */}
			{!loading && partners.length > 0 && (
				<div className="flex gap-4 mb-6">
					<div className="flex items-center gap-2 text-sm">
						<span className="w-2.5 h-2.5 rounded-full bg-green-500 inline-block" />
						<span className="text-gray-600 dark:text-gray-400">
							{activeCount} Active
						</span>
					</div>
					<div className="flex items-center gap-2 text-sm">
						<span className="w-2.5 h-2.5 rounded-full bg-gray-400 inline-block" />
						<span className="text-gray-600 dark:text-gray-400">
							{inactiveCount} Inactive
						</span>
					</div>
				</div>
			)}

			{/* Table */}
			{loading ? (
				<div className="flex items-center justify-center py-20">
					<RefreshCw className="w-6 h-6 animate-spin text-gray-400 mr-2" />
					<span className="text-gray-500 dark:text-gray-400">Loading partners…</span>
				</div>
			) : partners.length === 0 ? (
				<div className="text-center py-20 text-gray-400 dark:text-gray-500">
					<Truck className="w-10 h-10 mx-auto mb-3 opacity-40" />
					<p>No logistics partners found.</p>
				</div>
			) : (
				<div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
					<Table>
						<TableHeader>
							<TableRow className="bg-gray-50 dark:bg-gray-800">
								<TableHead className="font-semibold text-gray-700 dark:text-gray-300">
									Partner Name
								</TableHead>
								<TableHead className="font-semibold text-gray-700 dark:text-gray-300">
									SP ID
								</TableHead>
								<TableHead className="font-semibold text-gray-700 dark:text-gray-300">
									Status
								</TableHead>
								<TableHead className="font-semibold text-gray-700 dark:text-gray-300">
									Last Updated
								</TableHead>
								<TableHead className="font-semibold text-gray-700 dark:text-gray-300 text-center">
									Toggle
								</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{partners.map((partner) => (
								<TableRow
									key={partner.id}
									className="hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors"
								>
									<TableCell className="font-medium dark:text-white">
										<div className="flex items-center gap-2">
											<Truck className="w-4 h-4 text-gray-400" />
											{partner.name}
										</div>
									</TableCell>
									<TableCell className="text-gray-500 dark:text-gray-400 font-mono text-sm">
										{partner.sp_id}
									</TableCell>
									<TableCell>
										{partner.active ? (
											<Badge className="bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300 border-green-200 dark:border-green-800">
												Active
											</Badge>
										) : (
											<Badge
												variant="secondary"
												className="bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400"
											>
												Inactive
											</Badge>
										)}
									</TableCell>
									<TableCell className="text-gray-500 dark:text-gray-400 text-sm">
										{formatDate(partner.updated_at)}
									</TableCell>
									<TableCell className="text-center">
										<Switch
											id={`partner-toggle-${partner.id}`}
											checked={partner.active}
											disabled={togglingId === partner.id}
											onCheckedChange={(checked) =>
												handleToggle(partner, checked)
											}
										/>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</div>
			)}

			{/* Info note */}
			<div className="mt-6 p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800">
				<p className="text-sm text-blue-700 dark:text-blue-300 flex items-start gap-2">
					<Truck className="w-4 h-4 mt-0.5 flex-shrink-0" />
					<span>
						Disabling a logistics partner will immediately exclude all of their SLA
						lanes from live inventory calculations. Active orders are not affected.
					</span>
				</p>
			</div>
		</div>
	);
};

export default LogisticsPartners;
